import {
    getNumberOfFurtherRepayments,
    hasEqualRepaymentsFromSchedule
} from '@api/lendingAPI/CreditFacilityAPIHelper';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { isModalOpenInURL } from '@iwModals/ModalsHelper';
import {
    GetAccountsResponse,
    GetEarlyRepaymentSimulationResponse,
    GetPaymentMethodResponse
} from '@iwoca/lapi-client/edge';
import { getQueryParameter } from '@iwRouter/LocationHelper';

import { TState } from './EarlyRepayment.reducer';
import { OVERDUE_STATUS } from '../multiLoanDashboard/accountsSelector';

export function getEarlyRepaymentURLData(location: Location) {
    return {
        amount: parseFloat(getQueryParameter(location, 'amount')!),
        accountId: getQueryParameter(location, 'accountId'),
        showRepaymentSchedule: isModalOpenInURL(
            location,
            'RepaymentScheduleModal'
        ),
        step: getStep(location)
    };
}

export function getStep(location: Partial<Location>) {
    const pathToStep = {
        '/repayment/choose-amount/': 'CHOOSE_REPAYMENT_AMOUNT',
        '/repayment/confirm/': 'CONFIRM_REPAYMENT',
        '/repayment/truelayer/redirect/': 'ACCEPT_TRUELAYER_REDIRECT',
        '/repayment/truelayer/confirmation/': 'SHOW_TRUELAYER_CONFIRMATION'
    };

    for (const path in pathToStep) {
        if (location.pathname!.indexOf(path) === 0) {
            return pathToStep[path as keyof typeof pathToStep];
        }
    }
}

export function calculateOutstandingBalance(
    state: TState,
    action: {
        type: string;
        outstandingBalance: number;
        inputAmount: number;
    }
) {
    if (inputAmountIsGreaterThanBalance(state, action)) {
        return 0;
    }

    if (action.inputAmount && action.outstandingBalance) {
        return action.outstandingBalance - action.inputAmount;
    } else {
        return state.outstandingBalance! - action.inputAmount;
    }
}

function inputAmountIsGreaterThanBalance(
    state: TState,
    action: {
        type: string;
        outstandingBalance: number;
        inputAmount: number;
    }
) {
    if (action.outstandingBalance) {
        return action.inputAmount > action.outstandingBalance;
    }

    return action.inputAmount > state.outstandingBalance!;
}

export function serialiseInputAmount(
    inputAmount: number,
    outstandingBalance: number
) {
    if (inputAmount > outstandingBalance) {
        return outstandingBalance;
    } else {
        return inputAmount;
    }
}

export function getNextRepaymentAmount(repayment_schedule: {
    response: GetEarlyRepaymentSimulationResponse;
}) {
    return repayment_schedule?.response?.data?.summary?.simplified_schedule
        ?.next_repayment?.amount;
}

export function getNextRepaymentDate(repayment_schedule: {
    response: GetEarlyRepaymentSimulationResponse;
}) {
    return repayment_schedule?.response?.data?.summary?.simplified_schedule
        ?.next_repayment?.date;
}

export function getEqualRepaymentAmount(repayment_schedule: {
    response: GetEarlyRepaymentSimulationResponse;
}) {
    return repayment_schedule?.response?.data?.summary?.simplified_schedule
        ?.equal_repayments?.amount;
}

export function getPreviousScheduleAmount(repayment_schedule: {
    response: GetEarlyRepaymentSimulationResponse;
}) {
    return (repayment_schedule?.response?.data?.schedule).length;
}

export function getEqualRepaymentDate(repayment_schedule: {
    response: GetEarlyRepaymentSimulationResponse;
}) {
    return repayment_schedule?.response?.data?.summary?.simplified_schedule
        ?.equal_repayments?.day_of_month;
}

export function getEqualRepaymentInterval(repayment_schedule: {
    response: GetEarlyRepaymentSimulationResponse;
}) {
    return repayment_schedule?.response?.data?.summary?.simplified_schedule
        ?.equal_repayments?.interval;
}

export function getNextRepaymentStatus({
    initial,
    current
}: {
    initial: {
        response: GetEarlyRepaymentSimulationResponse;
    };
    current: {
        response: GetEarlyRepaymentSimulationResponse;
    };
}) {
    if (hasOnlyOneRepaymentScheduled(current)) {
        return null;
    } else if (
        hasInitialEqualRepayments(initial) &&
        !hasAPartialNextRepayment(initial, current)
    ) {
        return 'EXACT_AMOUNT';
    } else if (
        hasInitialEqualRepayments(initial) &&
        hasAPartialNextRepayment(initial, current) &&
        isNextRepaymentToday(current) &&
        hasSameNumberOfRepaymentsLeft(initial, current)
    ) {
        return 'PARTIAL_TODAY';
    } else if (
        hasInitialEqualRepayments(initial) &&
        hasAPartialNextRepayment(initial, current) &&
        hasSameNumberOfRepaymentsLeft(initial, current)
    ) {
        return 'PARTIAL_FUTURE';
    } else if (hasInitialEqualRepayments(initial)) {
        return 'NORMAL';
    }

    return null;
}

function hasInitialEqualRepayments(initial: {
    response: GetEarlyRepaymentSimulationResponse;
}) {
    const repayments = initial?.response?.data;
    return hasEqualRepaymentsFromSchedule(repayments);
}

function isNextRepaymentToday(current: {
    response: GetEarlyRepaymentSimulationResponse;
}) {
    const todayDate = new Date().toISOString().substring(0, 10);
    return current?.response?.data?.schedule?.[1]?.date === todayDate;
}

function hasAPartialNextRepayment(
    initial: {
        response: GetEarlyRepaymentSimulationResponse;
    },
    current: {
        response: GetEarlyRepaymentSimulationResponse;
    }
) {
    const currentAmount = current?.response?.data?.schedule?.[1]?.total;
    const equalRepaymentAmount =
        initial?.response?.data?.summary?.simplified_schedule?.equal_repayments
            ?.amount;
    return currentAmount < equalRepaymentAmount!;
}

function hasSameNumberOfRepaymentsLeft(
    initial: { response: GetEarlyRepaymentSimulationResponse },
    current: { response: GetEarlyRepaymentSimulationResponse }
) {
    const numberOfInitialRepayments =
        initial?.response?.data?.summary?.total_number_of_repayments;
    const numberOfCurrentRepayments =
        current?.response?.data?.summary?.total_number_of_repayments;
    return numberOfInitialRepayments === numberOfCurrentRepayments;
}

function hasOnlyOneRepaymentScheduled(current: {
    response: GetEarlyRepaymentSimulationResponse;
}) {
    return (current?.response?.data?.schedule ?? []).length === 1;
}

export function calculateNumberOfNextRepayments({
    initial,
    current
}: {
    initial: {
        response: GetEarlyRepaymentSimulationResponse;
    };
    current: {
        response: GetEarlyRepaymentSimulationResponse;
    };
}) {
    return (
        initial?.response?.data?.summary?.total_number_of_repayments! -
        current?.response?.data?.summary?.total_number_of_repayments!
    );
}

export function calculateFurtherMonthlyRepayments(current: {
    response: GetEarlyRepaymentSimulationResponse;
}) {
    if (hasMoreRepaymentLefts(current)) {
        return getNumberOfFurtherRepayments(current);
    }

    return null;
}

export function calculateReducedNextRepayment({
    initial,
    current
}: {
    initial: {
        response: GetEarlyRepaymentSimulationResponse;
    };
    current: {
        response: GetEarlyRepaymentSimulationResponse;
    };
}) {
    const nextRepayments = calculateNumberOfNextRepayments({
        initial,
        current
    });
    return nextRepayments + 1;
}

export function calculateFollowingRepaymentStatus({
    current
}: {
    current: {
        response: GetEarlyRepaymentSimulationResponse;
    };
}) {
    if (!hasMoreRepaymentLefts(current)) {
        return 'FULL_REPAYMENT';
    } else if (hasInitialEqualRepayments(current)) {
        return 'EQUAL';
    }
}

function hasMoreRepaymentLefts(current: {
    response: GetEarlyRepaymentSimulationResponse;
}) {
    return current?.response?.data?.summary?.total_number_of_repayments! > 1;
}

export function hasFollowingRepaymentsLeft(current: {
    response: GetEarlyRepaymentSimulationResponse;
}) {
    return current?.response?.data?.summary?.total_number_of_repayments! > 2;
}

export function calculateFollowingRepaymentAmount({
    current
}: {
    current: {
        response: GetEarlyRepaymentSimulationResponse;
    };
}) {
    return current?.response?.data?.schedule?.[2]?.total;
}

export function hasSameEqualRepaymentAmount({
    initial,
    current
}: {
    initial: {
        response: GetEarlyRepaymentSimulationResponse;
    };
    current: {
        response: GetEarlyRepaymentSimulationResponse;
    };
}) {
    const initialEqualRepaymentAmount =
        initial?.response?.data?.summary?.simplified_schedule?.equal_repayments
            ?.amount;
    const currentEqualRepaymentAmount =
        current?.response?.data?.summary?.simplified_schedule?.equal_repayments
            ?.amount;

    return initialEqualRepaymentAmount === currentEqualRepaymentAmount;
}

export const MANUAL_BANK_TRANSFER_TYPE = 'MANUAL_BANK_TRANSFER';
export const DEBIT_CARD_TYPE = 'DEBIT_CARD';
export const TRUELAYER_REPAYMENT_TYPE = 'TRUELAYER_REPAYMENT';

export const MANUAL_BANK_TRANSFER_OPTION = {
    type: MANUAL_BANK_TRANSFER_TYPE,
    label: 'Manual Bank Transfer',
    issuer: '',
    brand: '',
    cardLastDigits: '',
    value: 'manual_payment_id'
};

type TPaymentMethods = NonNullable<GetPaymentMethodResponse['data']>;

export function getPaymentMethodOptions({
    payment_method
}: {
    payment_method: TPaymentMethods;
}) {
    let options = [];

    if (getCountry() === 'UK') {
        options.push({
            type: TRUELAYER_REPAYMENT_TYPE,
            label: 'Instant Bank Transfer',
            issuer: '',
            brand: '',
            cardLastDigits: '',
            value: 'truelayer_repayment_id'
        });

        options = [
            ...options,
            ...payment_method
                .filter((method) => method.payment_method_type === 'card')
                .map((method) => ({
                    type: DEBIT_CARD_TYPE,
                    value: method.payment_method_id,
                    cardLastDigits: method.last_digits,
                    issuer: method.issuer,
                    brand: method.brand,
                    label: method.last_digits
                }))
        ];
    } else {
        // Manual Bank Transfers are pushed to the bottom of the page in the UK and should therefore only be in the list for DE
        options.push(MANUAL_BANK_TRANSFER_OPTION);
    }

    return {
        paymentOptions: options,
        selectedPayment: options[0]
    };
}

export function calculateReducedNextRepaymentAmount({
    current
}: {
    current: {
        response: GetEarlyRepaymentSimulationResponse;
    };
}) {
    return current?.response?.data?.summary?.simplified_schedule?.next_repayment
        ?.amount;
}

export function calculateDisableRepaymentMethodSelection() {
    if (getCountry() === 'DE') {
        return true;
    }
    // in the UK there are the options to repay via manual bank transfer and Truelayer
    return false;
}

export function getInitialRemainingOustandingBalance(
    account: NonNullable<
        NonNullable<GetAccountsResponse['data']>['accounts']
    >[number]
) {
    return account.balance!.total! - getInitialRepaymentAmount(account);
}

export function getInitialRepaymentAmount(
    account: NonNullable<
        NonNullable<GetAccountsResponse['data']>['accounts']
    >[number]
) {
    const isOverdue = isAccountOverdue(account);

    if (isOverdue) {
        return account.scheduled_payments!.schedule![0].total;
    }
    return account.balance!.total! / 2;
}

function isAccountOverdue(
    account: NonNullable<
        NonNullable<GetAccountsResponse['data']>['accounts']
    >[number]
) {
    const isLoanStatusOverdue = account.loan_status === OVERDUE_STATUS;
    const hasPositiveBalance = account.balance!.total !== 0;

    return Boolean(hasPositiveBalance && isLoanStatusOverdue);
}
